import { auth } from './firebase';
import { clientEnvironment } from 'shared/platform/environment';
import { uploadJson, UploadProgress } from './uploadJson';

/**
 * Typed HTTP client for the existing Aligned Cloud Functions. The Flutter app
 * calls them as plain HTTP at `${functionUrl}/<endpoint>` (NOT Firebase
 * callables); we mirror that here and attach the caller's Firebase ID token.
 *
 * Per-endpoint typed wrappers build on this shared request and retry base.
 *
 *
 * Retry/timeout matches aligned's `HttpService`: a default 30s timeout with
 * up to 3 attempts (exponential backoff: 1s, 2s, 4s) for transient failures —
 * network-level errors, our own timeout, or a 5xx response. 4xx responses and
 * any other error are never retried, matching aligned's
 * `_isTransientNetworkError`.
 */

const DEFAULT_TIMEOUT_MS = 30_000;
/** LLM-backed / long-running endpoints opt into this, matching aligned's `_extendedTimeoutMs`. */
export const EXTENDED_TIMEOUT_MS = 900_000;
const MAX_RETRY_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 1_000;

function baseUrl(): string {
  return clientEnvironment.functionsUrl.replace(/\/$/, '');
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** aligned's exponential backoff: baseDelay * 2^attempt (1s, 2s, 4s for attempts 0, 1, 2). */
function retryDelayMs(attempt: number): number {
  return BASE_RETRY_DELAY_MS * (1 << attempt);
}

/** A network-level fetch failure — never a 4xx/5xx response, those are checked separately by status code. */
function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'TimeoutError') return true;
  if (error instanceof TypeError) return true; // fetch's generic "network request failed"
  return false;
}

/**
 * The browser's report of a request that never got a response: Chromium's
 * "Failed to fetch", WebKit's "Load failed", Firefox's "NetworkError when
 * attempting to fetch resource.", and `uploadJson`'s XHR failure. These are
 * what aligned's Dio client words as "connection error" or, on the web,
 * "XMLHttpRequest onError" — the strings its callers match to tell a
 * connectivity failure from a bad response.
 */
export function isConnectionFailure(error: unknown): boolean {
  if (!(error instanceof TypeError)) return false;
  return /^(failed to fetch|load failed|networkerror when attempting to fetch resource|upload connection failed)/i
    .test(error.message);
}

export function timeoutError(): DOMException {
  return new DOMException('Request timed out', 'TimeoutError');
}

/**
 * Combines an optional caller-supplied `AbortSignal` with an internal
 * timeout into one signal for `fetch`. A caller-triggered abort propagates
 * immediately (never retried); our own timeout is treated as transient and
 * retried like aligned's `TimeoutException` handling.
 *
 * `timedOut()` reports whether *our* timer fired, rather than inspecting the
 * error fetch rejected with. `abort(reason)` is not honoured everywhere —
 * WebKit discards the reason and rejects with a bare `AbortError` — so a
 * timeout there would otherwise look like a caller cancellation: never retried,
 * and escaping as an unhandled `AbortError: The user aborted a request.`
 */
function withTimeout(
  externalSignal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void; timedOut: () => boolean } {
  const controller = new AbortController();
  let didTimeOut = false;
  const timeoutId = setTimeout(() => {
    didTimeOut = true;
    controller.abort(timeoutError());
  }, timeoutMs);

  const onExternalAbort = () => controller.abort(externalSignal?.reason);
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort(externalSignal.reason);
    else externalSignal.addEventListener('abort', onExternalAbort);
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    },
    timedOut: () => didTimeOut,
  };
}

export class FunctionsError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly endpoint: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'FunctionsError';
  }
}

/**
 * A fetch aborted by the browser/engine itself — not by a caller-supplied
 * signal, not by our own timeout — typically page teardown or navigation
 * mid-request. Distinct from `FunctionsError` so callers can treat it as
 * expected cancellation rather than a provisioning failure.
 */
export class RequestCancelledError extends Error {
  constructor(readonly endpoint: string) {
    super(`Cloud Function ${endpoint} request was cancelled`);
    this.name = 'RequestCancelledError';
  }
}

export interface CallFunctionOptions {
  onUploadProgress?: (progress: UploadProgress) => void;
  method?: 'POST' | 'GET';
  /** Send the Firebase ID token as a Bearer header (default true). */
  authenticated?: boolean;
  signal?: AbortSignal;
  /** Per-call timeout override in ms (default 30s, matching aligned). */
  timeoutMs?: number;
  /** Per-call retry-attempt override (default 3, matching aligned). Set to 1 to disable retry. */
  maxRetries?: number;
  /**
   * Absolute URL override for an endpoint that must bypass the shared functions
   * base. The only current user is the proxy-connection check, which the
   * experts' PAC file allowlists at its Cloud Run host (not the generic
   * `cloudfunctions.net` base) so the request egresses through the proxy. When
   * set, `endpoint` is used only for error messages, not URL construction.
   */
  absoluteUrl?: string;
}

export function functionUrl(endpoint: string): string {
  const path = endpoint.replace(/^\//, '');
  return `${baseUrl()}/${path}`;
}

async function authHeader(
  authenticated: boolean,
  forceRefresh = false,
): Promise<Record<string, string>> {
  if (!authenticated) return {};
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken(forceRefresh);
  return { Authorization: `Bearer ${token}` };
}

/**
 * Call a Cloud Function endpoint and parse the JSON response. Throws
 * `FunctionsError` on non-2xx so callers get an explicit, typed failure rather
 * than a silently-swallowed result. Retries transient failures (network
 * errors, our own timeout, 5xx responses) with exponential backoff; 4xx and
 * any other error are thrown immediately, matching aligned's `HttpService`.
 *
 * The one 4xx exception is a 401 on an authenticated call: the ID token is
 * force-refreshed and the request sent once more, outside the retry budget.
 * The SDK's cached token can look fresh client-side yet be rejected by the
 * function — a throttled background tab, clock skew, a rotated signing key —
 * and aligned's `verifyAuth` treats that as a routine token-lifecycle event
 * the client is expected to recover from by refreshing, as aligned's own
 * log transport does.
 */
export async function callFunction<T = unknown>(
  endpoint: string,
  body?: unknown,
  options: CallFunctionOptions = {},
): Promise<T> {
  const {
    method = 'POST',
    authenticated = true,
    signal: externalSignal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    maxRetries = MAX_RETRY_ATTEMPTS,
    absoluteUrl,
  } = options;
  const url = absoluteUrl ?? functionUrl(endpoint);
  let headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader(authenticated)),
  };
  let refreshedToken = false;

  for (let attempt = 0; ; attempt++) {
    const { signal, cleanup, timedOut } = withTimeout(externalSignal, timeoutMs);
    const isLastAttempt = attempt >= maxRetries - 1;
    let res: Pick<Response, 'ok' | 'status' | 'text'>;
    try {
      res = options.onUploadProgress && method === 'POST'
        ? await uploadJson(url, JSON.stringify(body ?? {}), headers, signal, options.onUploadProgress)
        : await fetch(url, {
        method,
        headers,
        body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
        signal,
      });
    } catch (err) {
      cleanup();
      if (externalSignal?.aborted) throw err; // caller cancelled — propagate immediately, never retried
      if ((timedOut() || isTransientNetworkError(err)) && !isLastAttempt) {
        await sleep(retryDelayMs(attempt));
        continue;
      }
      // Report our own timeout as a TimeoutError even where the engine
      // discarded the abort reason, so callers can tell it from a cancellation.
      if (timedOut()) throw timeoutError();
      // An AbortError we didn't cause (no external signal, not our timer)
      // means the browser cancelled the request itself — page teardown or
      // navigation. Surface it distinctly so callers can treat it as
      // expected, not a defect.
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new RequestCancelledError(endpoint);
      }
      throw err;
    }
    cleanup();

    const text = await res.text();
    const parsed = text ? safeJsonParse(text) : undefined;

    if (!res.ok) {
      if (res.status === 401 && headers.Authorization && !refreshedToken) {
        refreshedToken = true;
        headers = { ...headers, ...(await authHeader(authenticated, true)) };
        attempt--; // the refreshed resend does not spend a transient-retry attempt
        continue;
      }
      if (res.status >= 500 && !isLastAttempt) {
        await sleep(retryDelayMs(attempt));
        continue;
      }
      throw new FunctionsError(
        `Cloud Function ${endpoint} failed (${res.status})`,
        res.status,
        endpoint,
        parsed ?? text,
      );
    }

    return parsed as T;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------------------
// Typed endpoint wrappers. The full call catalog (docs/02) is filled in as each
// feature is ported; these are the cross-cutting ones used by the platform.
// ---------------------------------------------------------------------------

export type ClientRuntimeConfig = Record<string, unknown>;

export interface RecordLoginIpResult {
  success: boolean;
  ipAddress: string;
  lastUsedAt: string;
  added: boolean;
}

/** Best-effort audit record for the signed-in user's current client IP. */
export function recordLoginIp(): Promise<RecordLoginIpResult> {
  return callFunction<RecordLoginIpResult>('recordLoginIp', {});
}

/**
 * Cached across the session: these values are fixed per environment, and the
 * call sits on the critical path of opening the Persona embed. Only the
 * no-options form is cached, so a caller that passes its own signal or timeout
 * still gets a fresh request.
 */
let runtimeConfigPromise: Promise<ClientRuntimeConfig> | null = null;

/** Boot-time client configuration (mirrors aligned's getClientRuntimeConfig). */
export function getClientRuntimeConfig(
  options?: CallFunctionOptions,
): Promise<ClientRuntimeConfig> {
  if (options) return callFunction<ClientRuntimeConfig>('getClientRuntimeConfig', {}, options);
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = callFunction<ClientRuntimeConfig>('getClientRuntimeConfig', {}).catch(
      (error: unknown) => {
        // A failure must not be cached, or one flaky call would block
        // verification for the rest of the session.
        runtimeConfigPromise = null;
        throw error;
      },
    );
  }
  return runtimeConfigPromise;
}

export interface SetUserAdminFlagsResult {
  isOwner?: boolean;
  isFinanceAdmin?: boolean;
  success: boolean;
  isAdmin: boolean;
  isOperationsManager: boolean;
  isContractorAdmin: boolean;
}

/**
 * Admin-only endpoint. Flips `user_accounts.isAdmin`, `isOperationsManager`, or
 * `isContractorAdmin`
 * and the matching Firebase Auth custom claims in one server-side operation.
 */
export function setUserAdminFlags(
  userAccountId: string,
  flags: { isOwner?: boolean; isFinanceAdmin?: boolean; isAdmin?: boolean; isOperationsManager?: boolean; isContractorAdmin?: boolean },
  options?: CallFunctionOptions,
): Promise<SetUserAdminFlagsResult> {
  return callFunction<SetUserAdminFlagsResult>('setUserAdminFlags', { userAccountId, ...flags }, options);
}

/** Best-effort Persona inquiry redaction used by aligned account closure. */
export async function redactPersonaForUser(userAccountId: string): Promise<boolean> {
  try {
    const response = await callFunction<{ success?: boolean }>('redactPersonaForUser', { userAccountId });
    return response?.success === true;
  } catch {
    return false;
  }
}

/** Best-effort Firebase refresh-token revocation used by account closure. */
export async function revokeUserSessions(uid: string): Promise<boolean> {
  try {
    await callFunction('revokeUserSessions', { uid });
    return true;
  } catch {
    return false;
  }
}

/**
 * Stream a Cloud Function response (the `chat*Stream` endpoints emit
 * text/event-stream). Raw text chunks are delivered to `onChunk` as they arrive;
 * the caller parses SSE framing for its endpoint. Throws `FunctionsError` on a
 * non-2xx response so failures aren't silent.
 */
export async function callFunctionStream(
  endpoint: string,
  body: unknown,
  onChunk: (chunk: string) => void,
  options: CallFunctionOptions = {},
): Promise<void> {
  const { authenticated = true, signal } = options;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(await authHeader(authenticated)),
  };

  const res = await fetch(functionUrl(endpoint), {
    method: 'POST',
    headers,
    body: JSON.stringify(body ?? {}),
    signal,
  });

  if (!res.ok || !res.body) {
    const text = res.body ? await res.text() : '';
    throw new FunctionsError(
      `Cloud Function ${endpoint} stream failed (${res.status})`,
      res.status,
      endpoint,
      text ? safeJsonParse(text) : undefined,
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) onChunk(decoder.decode(value, { stream: true }));
  }
}
